import { useState } from "react";
import { Link } from "react-router-dom";
import {
  useFetchOrdersQuery,
  useTypedDispatch,
  useTypedSelector,
} from "../store";
import Pagination from "../components/common/Pagination";
import { FaExternalLinkAlt } from "react-icons/fa";
import { RiEBike2Line, RiMapPinUserLine } from "react-icons/ri";
import { MdOutlineShoppingBag } from "react-icons/md";
import { HiRefresh, HiOutlineFilter } from "react-icons/hi";
import { FeeTypeText, OrderStatus, OrderType } from "../types";
import RiderSelect from "../components/common/RiderSelect";
import OrderStatusSelect from "../components/common/OrderStatusSelect";
import { orderApi } from "../store/apis/order.api";
import { TagType, orderStatusOptions } from "../configs";
import Skeleton from "../components/common/Skeleton";
import RushOrders from "../components/common/RushOrders";
import useDebounce from "../hooks/useDebounce";
import classNames from "classnames";

const Orders: React.FC = () => {
  const dispatch = useTypedDispatch();
  const city = useTypedSelector((state) => state.session.city);
  const restaurant = useTypedSelector((state) => state.session.restaurant);

  // State
  const [allCities, setAllCities] = useState<boolean>(false);
  const [rush, setRush] = useState<boolean>(false);
  const [page, setPage] = useState<number>(1);
  const [perPage, setPerPage] = useState<number>(20);
  const [status, setStatus] = useState<string>("");
  const [startDate, setStartDate] = useState<string>("");
  const [endDate, setEndDate] = useState<string>("");
  const [displayId, setDisplayId] = useState<string>("");
  const debouncedDisplayId = useDebounce(displayId, 400);

  // Queries
  const {
    data: orders,
    refetch,
    isFetching,
  } = useFetchOrdersQuery(
    {
      page,
      perPage,
      displayId: debouncedDisplayId,
      restaurantId: restaurant?._id,
      status: status as OrderStatus,
      startDate,
      endDate,
      ...(allCities ? {} : { cityId: city?._id }),
    },
    {
      skip: !!debouncedDisplayId && debouncedDisplayId.length !== 6,
    }
  );

  const handleRefresh = () => {
    dispatch(
      orderApi.util.invalidateTags([{ type: TagType.Order, id: "LIST" }])
    );
    refetch();
  };

  return (
    <div className="w-full mx-auto p-6 bg-gray-50 min-h-screen">
      {/* Page Title & Actions */}
      <div className="flex flex-col md:flex-row md:items-center justify-between mb-6 gap-4">
        <div>
          <h1 className="text-2xl font-bold text-gray-800">Commandes</h1>
          <p className="text-sm text-gray-500">
            Gérez et suivez toutes les commandes en temps réel.
          </p>
        </div>

        <div className="flex items-center gap-3">
          <div className="flex items-center gap-3 bg-white p-1 rounded-lg border border-gray-200">
            <button
              onClick={() => setAllCities(false)}
              className={classNames(
                "px-4 py-1.5 rounded-md font-medium text-sm transition-all",
                !allCities
                  ? "bg-gray-100 text-gray-900 shadow-sm"
                  : "text-gray-500 hover:text-gray-700"
              )}
            >
              Ma ville
            </button>
            <button
              onClick={() => setAllCities(true)}
              className={classNames(
                "px-4 py-1.5 rounded-md font-medium text-sm transition-all",
                allCities
                  ? "bg-gray-100 text-gray-900 shadow-sm"
                  : "text-gray-500 hover:text-gray-700"
              )}
            >
              Toutes les villes
            </button>
          </div>

          <div className="flex items-center gap-2 bg-white p-1 rounded-lg border border-gray-200 h-full">
            <span className="text-sm font-medium text-gray-600 px-2">Rush</span>
            <button
              onClick={() => setRush(!rush)}
              className={classNames(
                "w-11 h-6 flex items-center rounded-full p-1 transition-colors duration-300 focus:outline-none",
                rush ? "bg-red-500" : "bg-gray-300"
              )}
            >
              <div
                className={classNames(
                  "bg-white w-4 h-4 rounded-full shadow-md transform transition-transform duration-300",
                  rush ? "translate-x-5" : "translate-x-0"
                )}
              />
            </button>
          </div>

          <button
            onClick={handleRefresh}
            className="flex items-center gap-2 px-4 py-2 bg-white border border-gray-200 text-gray-700 rounded-lg hover:bg-gray-50 transition-colors shadow-sm font-medium text-sm"
          >
            <HiRefresh
              className={classNames(
                "w-4 h-4",
                isFetching ? "animate-spin" : ""
              )}
            />
            Actualiser
          </button>
        </div>
      </div>

      {/* Rush Orders Section */}
      {rush && (
        <div className="mb-6 animate-in fade-in slide-in-from-top-4 duration-300">
          <RushOrders />
        </div>
      )}

      {/* Filters & Table Container */}
      <div className="bg-white shadow-sm border border-gray-200 rounded-xl overflow-hidden">
        {/* Filters Header */}
        <div className="p-4 border-b border-gray-100 bg-white grid grid-cols-1 md:grid-cols-12 gap-4 items-center">
          {/* Status Filter */}
          <div className="md:col-span-3">
            <div className="relative">
              <select
                value={status}
                onChange={(e) => setStatus(e.target.value)}
                className="w-full pl-3 pr-10 py-2 text-sm border-gray-200 rounded-lg focus:ring-2 focus:ring-primary/20 focus:border-primary transition-shadow bg-gray-50/50 appearance-none cursor-pointer"
              >
                <option value="">Tous les statuts</option>
                {orderStatusOptions
                  .filter((o) => o.value !== "")
                  .map((option) => (
                    <option
                      key={option.value}
                      value={option.value}
                    >
                      {option.label}
                    </option>
                  ))}
              </select>
              <HiOutlineFilter className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-400 pointer-events-none" />
            </div>
          </div>

          {/* Search ID */}
          <div className="md:col-span-3">
            <input
              type="text"
              value={displayId}
              onChange={(e) => setDisplayId(e.target.value)}
              placeholder="Rechercher par N° commande..."
              className="w-full px-3 py-2 text-sm border-gray-200 rounded-lg focus:ring-2 focus:ring-primary/20 focus:border-primary transition-shadow bg-gray-50/50"
            />
          </div>

          {/* Date Range */}
          <div className="md:col-span-6 flex items-center gap-2">
            <input
              type="date"
              value={startDate}
              onChange={(e) => setStartDate(e.target.value)}
              max={endDate}
              className="flex-1 px-3 py-2 text-sm border-gray-200 rounded-lg focus:ring-2 focus:ring-primary/20 focus:border-primary transition-shadow bg-gray-50/50"
            />
            <span className="text-gray-400">-</span>
            <input
              type="date"
              value={endDate}
              onChange={(e) => setEndDate(e.target.value)}
              min={startDate}
              className="flex-1 px-3 py-2 text-sm border-gray-200 rounded-lg focus:ring-2 focus:ring-primary/20 focus:border-primary transition-shadow bg-gray-50/50"
            />
            {(startDate || endDate) && (
              <button
                onClick={() => {
                  setStartDate("");
                  setEndDate("");
                }}
                className="text-xs text-red-500 hover:text-red-700 font-medium px-2"
              >
                Reset
              </button>
            )}
          </div>
        </div>

        {/* Table */}
        <div className="overflow-x-auto">
          <table className="w-full text-left border-collapse">
            <thead>
              <tr className="bg-gray-50/80 border-b border-gray-200 text-xs uppercase tracking-wider text-gray-500 font-semibold">
                <th className="px-6 py-4 w-24">ID</th>
                <th className="px-6 py-4 w-24 text-center">Type</th>
                <th className="px-6 py-4">Restaurant & Client</th>
                <th className="px-6 py-4 w-64">Livreur</th>
                <th className="px-6 py-4 w-48">Statut</th>
                <th className="px-6 py-4 text-right">Montant</th>
                <th className="px-6 py-4 text-center w-32">Date</th>
                <th className="px-6 py-4 w-16"></th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-100">
              {isFetching ? (
                <tr>
                  <td
                    colSpan={8}
                    className="p-6"
                  >
                    <Skeleton
                      times={5}
                      className="h-12 w-full mb-2"
                    />
                  </td>
                </tr>
              ) : orders?.data?.length === 0 ? (
                <tr>
                  <td
                    colSpan={8}
                    className="p-12 text-center text-gray-500 italic"
                  >
                    Aucune commande trouvée pour ces critères.
                  </td>
                </tr>
              ) : (
                orders?.data?.map((row) => (
                  <tr
                    key={row._id}
                    className="hover:bg-gray-50/50 transition-colors group"
                  >
                    <td className="px-6 py-4">
                      <span className="font-mono font-bold text-gray-700 bg-gray-100 px-2 py-1 rounded text-xs">
                        #{row.displayId}
                      </span>
                    </td>
                    <td className="px-6 py-4 text-center">
                      <div className="flex justify-center">
                        {row.orderType === OrderType.DELIVERY ? (
                          <div
                            title="Livraison"
                            className="bg-blue-50 text-blue-600 p-2 rounded-full"
                          >
                            <RiEBike2Line className="w-5 h-5" />
                          </div>
                        ) : (
                          <div
                            title="À emporter"
                            className="bg-orange-50 text-orange-600 p-2 rounded-full"
                          >
                            <MdOutlineShoppingBag className="w-5 h-5" />
                          </div>
                        )}
                      </div>
                    </td>
                    <td className="px-6 py-4">
                      <div className="flex flex-col">
                        <span className="font-medium text-gray-900 text-sm">
                          {row.restaurant}
                        </span>
                        <div className="flex items-center gap-1 text-xs text-gray-500 mt-1">
                          <RiMapPinUserLine className="w-3 h-3" />
                          {row.user}
                        </div>
                      </div>
                    </td>
                    <td className="px-6 py-4">
                      <RiderSelect
                        orderId={row._id}
                        value={row.rider || null}
                        placeholder="Assigner un livreur"
                      />
                    </td>
                    <td className="px-6 py-4">
                      <OrderStatusSelect
                        orderType={row.orderType}
                        orderId={row._id}
                        value={row.status}
                      />
                    </td>
                    <td className="px-6 py-4 text-right">
                      <div className="font-semibold text-gray-900">
                        {row.paymentInfo.totalPrice.text}
                      </div>
                      {row.paymentInfo.fees.length > 0 && (
                        <div className="flex flex-col gap-0.5 mt-0.5">
                          {row.paymentInfo.fees.map((fee, index) => (
                            <div
                              key={index}
                              className="text-xs text-gray-400"
                            >
                              {FeeTypeText[fee.type]}:{" "}
                              <span className="whitespace-nowrap">
                                {" "}
                                {fee.value.text}{" "}
                              </span>
                            </div>
                          ))}
                        </div>
                      )}
                    </td>
                    <td className="px-6 py-4 text-center text-xs text-gray-500">
                      {row.createdAt}
                    </td>
                    <td className="px-6 py-4 text-right">
                      <Link
                        to={row._id}
                        className="inline-flex items-center justify-center w-8 h-8 rounded-full bg-transparent hover:bg-primary/10 text-gray-400 hover:text-primary transition-all"
                        title="Voir les détails"
                      >
                        <FaExternalLinkAlt className="w-4 h-4" />
                      </Link>
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>

        {/* Footer / Pagination */}
        <div className="border-t border-gray-200 bg-gray-50 p-4">
          <Pagination
            paginationInfo={{
              page,
              perPage,
              count: orders?.paginationInfo.count || 1,
            }}
            setPage={setPage}
            setPerPage={setPerPage}
          />
        </div>
      </div>
    </div>
  );
};

export default Orders;
